import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { documentTitle } from '../lib/route-title'

/**
 * `RouteAnnouncer` - makes SPA navigation perceivable.
 *
 * A single-page app changes the whole screen without a document load, so
 * neither the window title nor keyboard focus moves on its own. the MUR "Design Tokens and Accessibility" page
 * Section 3 "Page transitions" requires both:
 *
 *   - `document.title` becomes "Murmuration - <section>";
 *   - keyboard focus moves to the new `<h1>`.
 *
 * Renders nothing. Mounted once inside `BrowserRouter`, above `<Routes>`,
 * so it covers gated and unauthenticated routes alike.
 *
 * Focus is deliberately NOT moved on first paint: on a cold load the user
 * has not navigated, and stealing focus from the document start would skip
 * the skip-link. It moves on every subsequent pathname change.
 *
 * The focus target carries `tabIndex={-1}` (set by each page's `<h1>`) so
 * it is programmatically focusable without joining the tab order. Its
 * focus ring is left in place: `:focus-visible` does not match when the
 * navigation came from a mouse click, so only keyboard users see it - and
 * for them the ring is the confirmation that focus moved.
 */
export function RouteAnnouncer(): null {
  const { pathname } = useLocation()
  const isFirstRender = useRef(true)

  useEffect(() => {
    document.title = documentTitle(pathname)

    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // Defer to the end of the frame so the new route's <h1> is mounted.
    const id = window.requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>('#main h1, main h1, h1')
      heading?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(id)
  }, [pathname])

  return null
}
